import { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  Alert,
  Divider,
  Chip,
  Stack,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";

import {
  Close,
  Search,
  Settings,
  HomeWork,
  Security,
  Gavel,
  ExpandMore,
  PersonOff,
  Build,
  Handshake,
  Balance,
  Notes,
  Apartment,
  Tune,
  AutoAwesome,
  ContentCopy,
  DoneAll,
  ClearAll,
  Lock,
} from "@mui/icons-material";

import {
  SETTING_PRESETS,
  TERMINATION_REASONS,
  NOTICE_PERIOD_OPTIONS,
  TENANT_PENALTY_OPTIONS,
  COMPENSATION_OPTIONS,
  REFUND_WITHIN_OPTIONS,
  DAMAGE_RECOVERY_OPTIONS,
  DISPUTE_METHOD_OPTIONS,
  getPreset,
  pickRuleFields,
  matchPreset,
  summarizeSetting,
  validateRules,
} from "../constants/propertySettingPresets";

const HEADER_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const DEFAULT_PRESET = "standard";

// Collapsible section wrapper so every part of this long form reads as one
// consistent "card" instead of a flat, undifferentiated scroll.
function Section({ title, icon, defaultExpanded, error, children }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      elevation={0}
      sx={{
        borderRadius: "16px !important",
        border: "1px solid",
        borderColor: error ? "#fca5a5" : "#e5e7eb",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          bgcolor: error ? "#fef2f2" : "#f8fafc",
          "&:hover": { bgcolor: error ? "#fee2e2" : "#f1f5f9" },
          minHeight: 60,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "10px",
              background: error ? "#dc2626" : HEADER_GRADIENT,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {error && (
            <Chip size="small" color="error" label="Needs attention" sx={{ ml: 1 }} />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3, bgcolor: "#fff" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// Numbered heading so the three things a landlord actually has to do (name it,
// pick a rule template, choose properties) read as a short checklist rather
// than one undifferentiated wall of inputs.
function StepHeader({ number, title, caption, action }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: HEADER_GRADIENT,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="body2" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

// A property can take a NEW setting only if it isn't rented out, wasn't
// rejected, and isn't already covered by another setting. A setting has to be
// applied BEFORE the property goes to admin, so not-yet-approved flats DO
// count. Exported so the Property Settings page can gate its "Add New Setting"
// button on exactly the rule this modal uses to build the list — otherwise the
// button opens a modal with nothing to pick.
// `is_rent` comes back as 1 or "1" depending on the endpoint (the registration
// schema types it as a string), so it has to be coerced — a bare `!== 1` lets
// every rented flat through.
export const isRented = (property) => Number(property?.is_rent) === 1;

export const isAvailableForNewSetting = (property) =>
  !isRented(property) &&
  !property?.is_rejected &&
  !property?.is_property_settings;

export default function PropertySettingModal({
  open,
  handleClose,
  properties = [],
  handleSave,
  userId,
  existingSettings = [],
  selectedSetting,
  viewOnly = false,
  setSelectedSetting,
  saving = false,
  /* Flats to tick on open — used when the owner arrives straight from
     "add property", so the flat they just created is already selected. */
  initialProperties = [],
}) {
  // Rule fields only — name/description are kept separate so that switching a
  // preset never clobbers what the landlord already typed.
  const [rules, setRules] = useState(() => getPreset(DEFAULT_PRESET).values);
  const [settingName, setSettingName] = useState("");
  const [description, setDescription] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  const [selectedProperties, setSelectedProperties] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState({});
  const [copyFrom, setCopyFrom] = useState("");

  const presetKey = useMemo(() => matchPreset(rules), [rules]);
  const activePreset = getPreset(presetKey);
  const summary = useMemo(() => summarizeSetting(rules), [rules]);

  useEffect(() => {
    if (selectedSetting) {
      setRules(pickRuleFields(selectedSetting));
      setSettingName(selectedSetting.settingName || "");
      setDescription(selectedSetting.description || "");
      setSelectedProperties(selectedSetting.properties || []);
      setNameTouched(true);
      setShowAdvanced(true);
    } else if (initialProperties.length) {
      setSelectedProperties(initialProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSetting]);

  // Suggest a name that follows the chosen template until the landlord types
  // their own — one less empty required field to think about.
  useEffect(() => {
    if (viewOnly || nameTouched || !activePreset) return;
    setSettingName(`${activePreset.label} Rules`);
  }, [activePreset, nameTouched, viewOnly]);

  const applyPreset = (key) => {
    setRules(getPreset(key).values);
    setErrors({});
    setCopyFrom("");
  };

  const applyExistingSetting = (id) => {
    setCopyFrom(id);
    const source = existingSettings.find((s) => s._id === id);
    if (source) {
      setRules(pickRuleFields(source));
      setErrors({});
    }
  };

  const setRule = (name, value) => {
    setRules((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleChange = (e) => setRule(e.target.name, e.target.value);

  const handleCheckbox = (e) => setRule(e.target.name, e.target.checked);

  const handleReason = (reason) => {
    const exist = rules.terminationReasons.includes(reason);
    setRule(
      "terminationReasons",
      exist
        ? rules.terminationReasons.filter((r) => r !== reason)
        : [...rules.terminationReasons, reason]
    );
  };

  const handlePropertySelect = (id) => {
    setSelectedProperties((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
    setErrors((prev) => {
      if (!prev.properties) return prev;
      const next = { ...prev };
      delete next.properties;
      return next;
    });
  };

  // Properties already covered by another setting — shown but locked, so the
  // landlord can see why a flat is missing instead of it silently vanishing.
  const assignedPropertyIds = useMemo(() => {
    const ids = new Set();
    existingSettings.forEach((setting) => {
      if (selectedSetting && setting._id === selectedSetting._id) return;
      (setting.properties || []).forEach((id) => ids.add(id));
    });
    return ids;
  }, [existingSettings, selectedSetting]);

  const filteredProperties = useMemo(() => {
    // A setting has to be applied BEFORE the property goes to admin, so this
    // list must include not-yet-approved flats. Rejected ones are out — they
    // cannot go to admin at all until the owner fixes them.
    //
    // Rented flats and flats already covered by another setting stay in the
    // list but come back locked (see `lockReason`). Dropping them silently was
    // the confusing part: the owner saw a shorter list than they own and had no
    // way to tell whether the flat was missing, rented, or already handled.
    let availableProperties = properties.filter((item) => !item.is_rejected);

    // if an existing setting is open, show only that setting's properties
    if (selectedSetting) {
      availableProperties = availableProperties.filter((property) =>
        selectedSetting.properties?.includes(property._id)
      );
    }

    if (!search) return availableProperties;

    return availableProperties.filter((item) => {
      const value = `
      ${item.flatName || ""}
      ${item.flatNumber || ""}
      ${item.propertyAddress || ""}
      ${item.city || ""}
    `.toLowerCase();

      return value.includes(search.toLowerCase());
    });
  }, [search, properties, selectedSetting]);

  /* Why a flat in the list can't be picked. Returning the reason instead of a
     bare boolean means the card can say it out loud. */
  const lockReason = (property) => {
    if (viewOnly) return "";
    if (isRented(property)) {
      return "Rented out — rules can only be changed once the flat is vacant.";
    }
    if (
      assignedPropertyIds.has(property._id) ||
      property.is_property_settings
    ) {
      return "Already covered by another setting. Delete that setting to reassign this flat.";
    }
    return "";
  };

  const selectableProperties = useMemo(
    () => filteredProperties.filter((p) => !lockReason(p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredProperties, assignedPropertyIds, viewOnly]
  );

  const allSelected =
    selectableProperties.length > 0 &&
    selectableProperties.every((p) => selectedProperties.includes(p._id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedProperties((prev) =>
        prev.filter((id) => !selectableProperties.some((p) => p._id === id))
      );
    } else {
      setSelectedProperties((prev) => [
        ...new Set([...prev, ...selectableProperties.map((p) => p._id)]),
      ]);
    }
  };

  const duplicateName = useMemo(
    () =>
      existingSettings.some(
        (s) =>
          s._id !== selectedSetting?._id &&
          (s.settingName || "").trim().toLowerCase() ===
            settingName.trim().toLowerCase()
      ),
    [existingSettings, selectedSetting, settingName]
  );

  const onSubmit = async () => {
    const nextErrors = validateRules(rules);

    if (!settingName.trim()) {
      nextErrors.settingName = "Give this rule set a name";
    } else if (duplicateName) {
      nextErrors.settingName = "You already have a setting with this name";
    }

    if (selectedProperties.length === 0) {
      nextErrors.properties = "Select at least one property";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      // Anything invalid lives inside the collapsed sections, so open them
      // rather than leaving the landlord staring at an error they cannot see.
      const onlyTopLevel = Object.keys(nextErrors).every((k) =>
        ["settingName", "properties"].includes(k)
      );
      if (!onlyTopLevel) setShowAdvanced(true);
      return;
    }

    handleSave({
      ...rules,
      settingName: settingName.trim(),
      description: description.trim(),
      properties: selectedProperties,
      userId,
    });
  };

  const fieldsDisabled = viewOnly;
  const ruleErrorCount = Object.keys(errors).filter(
    (k) => !["settingName", "properties"].includes(k)
  ).length;

  const emptyListMessage = () => {
    if (properties.length === 0) {
      return "You haven't added any properties yet. Add a property first, then apply a setting to it.";
    }
    if (filteredProperties.length === 0) {
      return "No property matches your search.";
    }
    const rentedCount = filteredProperties.filter(isRented).length;
    if (rentedCount === filteredProperties.length) {
      return "Every property here is rented out. Rules can be applied again once a flat is vacant.";
    }
    return "Nothing left to pick: these flats are either rented out or already covered by another setting.";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 0,
          background: HEADER_GRADIENT,
        }}
      >
        <Box
          sx={{
            p: 3,
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                bgcolor: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Settings sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {viewOnly
                  ? "Property Setting Details"
                  : "Add New Property Setting"}
              </Typography>

              <Typography sx={{ opacity: 0.9, mt: 0.5 }}>
                {viewOnly
                  ? "Read-only view of this saved setting"
                  : "Pick a template, choose your properties — that's it. Fine-tune only if you need to."}
              </Typography>
            </Box>
          </Box>

          <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          bgcolor: "#f1f5f9",
          p: 3,
        }}
      >
        <Grid container spacing={2}>
          {/* Step 1 — Name */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid #e5e7eb",
                boxShadow: "none",
              }}
            >
              <StepHeader
                number={1}
                title="Name this rule set"
                caption="You can reuse it on any number of properties later."
              />

              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="Setting Name"
                    value={settingName}
                    onChange={(e) => {
                      setNameTouched(true);
                      setSettingName(e.target.value);
                      setErrors((prev) => {
                        if (!prev.settingName) return prev;
                        const next = { ...prev };
                        delete next.settingName;
                        return next;
                      });
                    }}
                    disabled={fieldsDisabled}
                    error={Boolean(errors.settingName)}
                    helperText={errors.settingName}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    label="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={fieldsDisabled}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Step 2 — Template */}
          {!viewOnly && (
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                }}
              >
                <StepHeader
                  number={2}
                  title="Choose a rule template"
                  caption="Every field below is filled for you. Change one and it becomes a custom set."
                  action={
                    existingSettings.length > 0 && (
                      <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Start from existing</InputLabel>
                        <Select
                          label="Start from existing"
                          value={copyFrom}
                          onChange={(e) => applyExistingSetting(e.target.value)}
                          startAdornment={
                            <InputAdornment position="start">
                              <ContentCopy fontSize="small" />
                            </InputAdornment>
                          }
                        >
                          {existingSettings.map((s) => (
                            <MenuItem key={s._id} value={s._id}>
                              {s.settingName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )
                  }
                />

                <Grid container spacing={2}>
                  {SETTING_PRESETS.map((preset) => {
                    const active = presetKey === preset.key;
                    return (
                      <Grid item xs={12} md={4} key={preset.key}>
                        <Paper
                          onClick={() => applyPreset(preset.key)}
                          sx={{
                            p: 2.5,
                            height: "100%",
                            cursor: "pointer",
                            borderRadius: 3,
                            border: active
                              ? `2px solid ${preset.accent}`
                              : "1px solid #e5e7eb",
                            bgcolor: active
                              ? `${preset.accent}0d`
                              : "#fff",
                            boxShadow: "none",
                            transition: "0.2s",
                            "&:hover": {
                              borderColor: preset.accent,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography fontWeight={700} fontSize={17}>
                              {preset.label}
                            </Typography>
                            <Chip
                              size="small"
                              label={preset.tagline}
                              sx={{
                                bgcolor: `${preset.accent}1a`,
                                color: preset.accent,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {preset.description}
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>

                {presetKey === "custom" && (
                  <Alert severity="info" icon={<Tune />} sx={{ mt: 2 }}>
                    You've customised these rules. Pick a template above to
                    reset them.
                  </Alert>
                )}
              </Paper>
            </Grid>
          )}

          {/* Summary + advanced toggle */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid #e5e7eb",
                boxShadow: "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <AutoAwesome sx={{ color: "#667eea" }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    What these rules mean
                  </Typography>
                </Box>

                <Button
                  variant={showAdvanced ? "contained" : "outlined"}
                  startIcon={<Tune />}
                  onClick={() => setShowAdvanced((prev) => !prev)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    ...(showAdvanced
                      ? { background: HEADER_GRADIENT }
                      : { borderColor: "#667eea", color: "#667eea" }),
                  }}
                >
                  {showAdvanced
                    ? "Hide detailed rules"
                    : viewOnly
                    ? "Show detailed rules"
                    : "Customize rules"}
                </Button>
              </Box>

              <Stack spacing={1}>
                {summary.map((row) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexWrap: "wrap",
                      py: 1,
                      borderBottom: "1px dashed #e5e7eb",
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ minWidth: 190 }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: 1, minWidth: 220 }}
                    >
                      {row.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {ruleErrorCount > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {ruleErrorCount} rule
                  {ruleErrorCount > 1 ? "s need" : " needs"} a value — see the
                  highlighted sections below.
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Detailed rules — hidden by default */}
          {showAdvanced && (
            <>
              {/* Tenant Exit */}
              <Grid item xs={12}>
                <Section
                  title="Early Termination By Tenant"
                  icon={<PersonOff fontSize="small" />}
                  error={Boolean(
                    errors.tenantNoticePeriod ||
                      errors.tenantPenalty ||
                      errors.tenantCustomPenalty
                  )}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl
                        fullWidth
                        disabled={fieldsDisabled}
                        error={Boolean(errors.tenantNoticePeriod)}
                      >
                        <InputLabel>Notice Period</InputLabel>

                        <Select
                          label="Notice Period"
                          name="tenantNoticePeriod"
                          value={rules.tenantNoticePeriod}
                          onChange={handleChange}
                        >
                          {NOTICE_PERIOD_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.tenantNoticePeriod && (
                          <FormHelperText>
                            {errors.tenantNoticePeriod}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl
                        fullWidth
                        disabled={fieldsDisabled}
                        error={Boolean(errors.tenantPenalty)}
                      >
                        <InputLabel>Penalty</InputLabel>

                        <Select
                          label="Penalty"
                          name="tenantPenalty"
                          value={rules.tenantPenalty}
                          onChange={handleChange}
                        >
                          {TENANT_PENALTY_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.tenantPenalty && (
                          <FormHelperText>{errors.tenantPenalty}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Only relevant once "Custom" is chosen — no point asking
                        for an amount that will be ignored. */}
                    {rules.tenantPenalty === "Custom" && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Custom Penalty"
                          name="tenantCustomPenalty"
                          value={rules.tenantCustomPenalty}
                          onChange={handleChange}
                          disabled={fieldsDisabled}
                          error={Boolean(errors.tenantCustomPenalty)}
                          helperText={errors.tenantCustomPenalty}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">Rs</InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rules.deductSecurity}
                          name="deductSecurity"
                          onChange={handleCheckbox}
                          disabled={fieldsDisabled}
                        />
                      }
                      label="Deduct Security Deposit"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rules.deductAdvance}
                          name="deductAdvance"
                          onChange={handleCheckbox}
                          disabled={fieldsDisabled}
                        />
                      }
                      label="Deduct Advance Rent"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rules.tenantPaysSeparately}
                          name="tenantPaysSeparately"
                          onChange={handleCheckbox}
                          disabled={fieldsDisabled}
                        />
                      }
                      label="Tenant Pays Separately"
                    />
                  </FormGroup>
                </Section>
              </Grid>

              {/* Owner Exit */}
              <Grid item xs={12}>
                <Section
                  title="Early Termination By Owner"
                  icon={<HomeWork fontSize="small" />}
                  error={Boolean(
                    errors.ownerNoticePeriod || errors.ownerCustomCompensation
                  )}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl
                        fullWidth
                        disabled={fieldsDisabled}
                        error={Boolean(errors.ownerNoticePeriod)}
                      >
                        <InputLabel>Notice Period</InputLabel>

                        <Select
                          label="Notice Period"
                          name="ownerNoticePeriod"
                          value={rules.ownerNoticePeriod}
                          onChange={handleChange}
                        >
                          {NOTICE_PERIOD_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.ownerNoticePeriod && (
                          <FormHelperText>
                            {errors.ownerNoticePeriod}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth disabled={fieldsDisabled}>
                        <InputLabel>Compensation</InputLabel>

                        <Select
                          label="Compensation"
                          name="ownerCompensation"
                          value={rules.ownerCompensation}
                          onChange={handleChange}
                        >
                          {COMPENSATION_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {rules.ownerCompensation === "Custom" && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Custom Compensation"
                          name="ownerCustomCompensation"
                          value={rules.ownerCustomCompensation}
                          onChange={handleChange}
                          disabled={fieldsDisabled}
                          error={Boolean(errors.ownerCustomCompensation)}
                          helperText={errors.ownerCustomCompensation}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">Rs</InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Typography fontWeight={600} mb={1}>
                    Refund Policy
                  </Typography>

                  <RadioGroup
                    row
                    name="refundPolicy"
                    value={rules.refundPolicy}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="full"
                      control={<Radio disabled={fieldsDisabled} />}
                      label="Full Refund"
                    />

                    <FormControlLabel
                      value="dues"
                      control={<Radio disabled={fieldsDisabled} />}
                      label="Deduct Outstanding Dues"
                    />
                  </RadioGroup>
                </Section>
              </Grid>

              {/* Immediate Termination Reasons */}
              <Grid item xs={12}>
                <Section
                  title="Immediate Termination Reasons"
                  icon={<Gavel fontSize="small" />}
                  error={Boolean(errors.terminationReasons)}
                >
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    If a tenant is found responsible for any of these, the
                    agreement can be terminated immediately without the standard
                    notice period.
                  </Typography>
                  <Grid container spacing={1}>
                    {TERMINATION_REASONS.map((reason) => (
                      <Grid item xs={12} sm={6} md={4} key={reason}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={rules.terminationReasons.includes(reason)}
                              onChange={() => handleReason(reason)}
                              disabled={fieldsDisabled}
                            />
                          }
                          label={reason}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {errors.terminationReasons && (
                    <FormHelperText error>
                      {errors.terminationReasons}
                    </FormHelperText>
                  )}
                </Section>
              </Grid>

              {/* Security Deposit */}
              <Grid item xs={12}>
                <Section
                  title="Security Deposit Settlement"
                  icon={<Security fontSize="small" />}
                  error={Boolean(errors.refundWithin)}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl
                        fullWidth
                        disabled={fieldsDisabled}
                        error={Boolean(errors.refundWithin)}
                      >
                        <InputLabel>Refund Within</InputLabel>

                        <Select
                          label="Refund Within"
                          name="refundWithin"
                          value={rules.refundWithin}
                          onChange={handleChange}
                        >
                          {REFUND_WITHIN_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.refundWithin && (
                          <FormHelperText>{errors.refundWithin}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={rules.inspectionRequired}
                              name="inspectionRequired"
                              onChange={handleCheckbox}
                              disabled={fieldsDisabled}
                            />
                          }
                          label="Property Inspection Required"
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={rules.utilityClearance}
                              name="utilityClearance"
                              onChange={handleCheckbox}
                              disabled={fieldsDisabled}
                            />
                          }
                          label="Utility Bills Cleared"
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={rules.keyReturn}
                              name="keyReturn"
                              onChange={handleCheckbox}
                              disabled={fieldsDisabled}
                            />
                          }
                          label="Keys Returned"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Section>
              </Grid>

              {/* Damage Charges */}
              <Grid item xs={12}>
                <Section
                  title="Damage Charges"
                  icon={<Build fontSize="small" />}
                  error={Boolean(errors.damageRecovery)}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        disabled={fieldsDisabled}
                        error={Boolean(errors.damageRecovery)}
                      >
                        <InputLabel>Recovery Method</InputLabel>

                        <Select
                          label="Recovery Method"
                          name="damageRecovery"
                          value={rules.damageRecovery}
                          onChange={handleChange}
                        >
                          {DAMAGE_RECOVERY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.damageRecovery && (
                          <FormHelperText>
                            {errors.damageRecovery}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Alert severity="info">
                        Damage charges will be adjusted according to the selected
                        recovery method.
                      </Alert>
                    </Grid>
                  </Grid>
                </Section>
              </Grid>

              {/* Mutual Termination */}
              <Grid item xs={12}>
                <Section
                  title="Mutual Termination"
                  icon={<Handshake fontSize="small" />}
                  error={Boolean(errors.mutualPenalty)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rules.allowMutualTermination}
                        name="allowMutualTermination"
                        onChange={handleCheckbox}
                        disabled={fieldsDisabled}
                      />
                    }
                    label="Allow Mutual Termination"
                  />

                  {/* Penalty only matters when mutual termination is on. */}
                  {rules.allowMutualTermination && (
                    <Box mt={2} maxWidth={400}>
                      <FormControl
                        fullWidth
                        disabled={fieldsDisabled}
                        error={Boolean(errors.mutualPenalty)}
                      >
                        <InputLabel>Penalty</InputLabel>

                        <Select
                          label="Penalty"
                          name="mutualPenalty"
                          value={rules.mutualPenalty}
                          onChange={handleChange}
                        >
                          {COMPENSATION_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.mutualPenalty && (
                          <FormHelperText>{errors.mutualPenalty}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  )}
                </Section>
              </Grid>

              {/* Dispute Resolution */}
              <Grid item xs={12}>
                <Section
                  title="Dispute Resolution"
                  icon={<Balance fontSize="small" />}
                  error={Boolean(errors.disputeMethod)}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        disabled={fieldsDisabled}
                        error={Boolean(errors.disputeMethod)}
                      >
                        <InputLabel>Resolution Method</InputLabel>

                        <Select
                          label="Resolution Method"
                          name="disputeMethod"
                          value={rules.disputeMethod}
                          onChange={handleChange}
                        >
                          {DISPUTE_METHOD_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.disputeMethod && (
                          <FormHelperText>{errors.disputeMethod}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Alert severity="warning">
                        Select the preferred method for resolving rental
                        disputes.
                      </Alert>
                    </Grid>
                  </Grid>
                </Section>
              </Grid>

              {/* Additional Rules */}
              <Grid item xs={12}>
                <Section
                  title="Additional Rules"
                  icon={<Notes fontSize="small" />}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Write additional terms and conditions..."
                    name="additionalRules"
                    value={rules.additionalRules}
                    onChange={handleChange}
                    disabled={fieldsDisabled}
                  />
                </Section>
              </Grid>
            </>
          )}

          {/* Step 3 — Apply Properties */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: errors.properties ? "#fca5a5" : "#e5e7eb",
                boxShadow: "none",
              }}
            >
              <StepHeader
                number={viewOnly ? 2 : 3}
                title="Apply on properties"
                caption={
                  viewOnly
                    ? "Properties this setting is currently applied to."
                    : "Pick every property that should follow these rules."
                }
                action={
                  !viewOnly &&
                  selectableProperties.length > 0 && (
                    <Button
                      size="small"
                      startIcon={allSelected ? <ClearAll /> : <DoneAll />}
                      onClick={toggleSelectAll}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {allSelected ? "Clear all" : "Select all"}
                    </Button>
                  )
                }
              />

              {!viewOnly && (
                <TextField
                  fullWidth
                  placeholder="Search property..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />
              )}

              {!viewOnly && selectableProperties.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {emptyListMessage()}
                </Alert>
              )}

              <Grid container spacing={2}>
                {filteredProperties.map((property) => {
                  const reason = lockReason(property);
                  const locked = Boolean(reason);
                  const checked = selectedProperties.includes(property._id);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={property._id}>
                      <Paper
                        onClick={() => {
                          if (!viewOnly && !locked) {
                            handlePropertySelect(property._id);
                          }
                        }}
                        sx={{
                          p: 2,
                          height: "100%",
                          cursor: viewOnly || locked ? "default" : "pointer",
                          borderRadius: 3,
                          opacity: locked ? 0.72 : 1,
                          bgcolor: locked ? "#f8fafc" : "#fff",
                          border: checked
                            ? "2px solid var(--owner-primary)"
                            : "1px solid #e5e7eb",
                          transition: "0.3s",
                          "&:hover":
                            viewOnly || locked
                              ? {}
                              : { boxShadow: "0 6px 15px rgba(0,0,0,0.08)" },
                        }}
                      >
                        {locked && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              mb: 1.5,
                              px: 1.25,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: "#f1f5f9",
                              color: "#475569",
                            }}
                          >
                            <Lock sx={{ fontSize: 16, flexShrink: 0 }} />
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, lineHeight: 1.35 }}
                            >
                              {reason}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          {/* Image */}
                          <Box
                            component="img"
                            src={
                              property.images?.[0] ||
                              "https://via.placeholder.com/80"
                            }
                            alt={property.flatName || "Property"}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 2,
                              objectFit: "cover",
                            }}
                          />

                          {/* Details */}
                          <Box flex={1}>
                            <Typography fontWeight={700} fontSize={16}>
                              {property.flatName}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              Flat No: {property.flatNumber}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              Floor: {property.flatFloor}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              {property.flatRooms} Rooms • {property.flatToilet}{" "}
                              Bath
                            </Typography>
                          </Box>

                          <Checkbox
                            disabled={viewOnly || locked}
                            checked={checked}
                          />
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {property.propertyAddress}
                        </Typography>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mt={1.5}
                        >
                          <Typography fontWeight={700} color="#667eea">
                            Rs {property.flatRent}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {property.city}
                          </Typography>
                        </Box>

                        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={
                              isRented(property)
                                ? "Rented"
                                : property.is_approved
                                ? "Available"
                                : "Pending approval"
                            }
                            sx={{
                              bgcolor:
                                isRented(property)
                                  ? "#fee2e2"
                                  : property.is_approved
                                  ? "#dcfce7"
                                  : "#fef3c7",
                              color:
                                isRented(property)
                                  ? "#dc2626"
                                  : property.is_approved
                                  ? "#16a34a"
                                  : "#b45309",
                              fontWeight: 600,
                            }}
                          />

                          <Chip size="small" label={property.allowedFor} />

                          {locked && (
                            <Chip
                              size="small"
                              color="default"
                              label="Setting already applied"
                            />
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              <Box mt={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Chip
                  sx={{
                    background: HEADER_GRADIENT,
                    color: "#fff",
                    fontWeight: 600,
                  }}
                  label={`${selectedProperties.length} Properties Selected`}
                />
                {errors.properties && (
                  <Typography variant="body2" color="error">
                    {errors.properties}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: "#fff",
        }}
      >
        <Button variant="outlined" onClick={handleClose} disabled={saving}>
          {viewOnly ? "Close" : "Cancel"}
        </Button>

        {!viewOnly && (
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : null
            }
            sx={{
              background: HEADER_GRADIENT,
              "&:hover": { background: HEADER_GRADIENT, opacity: 0.9 },
            }}
          >
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
